import { Flex, Switch, theme, Form } from 'antd';
import PropTypes from 'prop-types';
import AntdFormItem from '../../../../../../../../../components/AntdFormItem';
import { useIntl } from 'react-intl';
import AntdSelect from '../../../../../../../../../components/AntdSelect';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import AntdInput from '../../../../../../../../../components/AntdInput';

const BatchForm = ({
  form,
  onValuesChange,
  structuralElements,
  availableBatches,
  allowUnbatch,
  normative,
  deliveryNote,
  fetchingBatches,
}) => {
  const intl = useIntl();
  const { token } = theme.useToken();
  const [isCertifiedDispersionChecked, setIsCertifiedDispersionChecked] =
    useState();

  const certifiedDis = form.getFieldValue('certified_dis');

  useEffect(() => {
    setIsCertifiedDispersionChecked(
      form.getFieldValue('certified_dis') || false,
    );
  }, [form, certifiedDis]);

  return (
    <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
      <Flex gap={20} style={{ marginBottom: '1em' }}>
        <AntdFormItem
          label={intl.formatMessage({ id: 'control_type' })}
          name="control_type"
          style={{ flex: 1 }}
          rules={[
            {
              required: true,
              message: intl.formatMessage({ id: 'required' }),
            },
          ]}
        >
          <AntdSelect
            options={
              deliveryNote?.mash_control && !deliveryNote?.mash_control_extra
                ? [
                    {
                      label: intl.formatMessage({
                        id: 'control_type.statistic',
                      }),
                      value: 'Estadístico',
                    },
                    {
                      label: intl.formatMessage({ id: 'control_type.100%' }),
                      value: '100%',
                    },
                  ]
                : [
                    {
                      label: intl.formatMessage({
                        id: 'control_type.statistic',
                      }),
                      value: 'Estadístico',
                    },
                  ]
            }
          />
        </AntdFormItem>
        <AntdFormItem
          name="structural_element"
          style={{ flex: 1 }}
          label={intl.formatMessage({ id: 'structural_element' })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({ id: 'required' }),
            },
          ]}
        >
          <AntdSelect
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'required',
                }),
              },
            ]}
            options={structuralElements?.map((element) => ({
              label: element.structural_element,
              value: element.structural_element,
            }))}
          />
        </AntdFormItem>
      </Flex>
      <Flex gap={20} style={{ marginBottom: '1em' }}>
        <AntdFormItem
          label={intl.formatMessage({ id: 'batch_code' })}
          name="batch_code"
          style={{ flex: '0 0 49%' }}
          rules={
            deliveryNote.concrete_batch
              ? [
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'required' }),
                  },
                ]
              : undefined
          }
        >
          <AntdSelect
            optionLabelProp="label"
            loading={fetchingBatches}
            options={[
              {
                value: 'newBatch',
                label: (
                  <Flex gap={5} align="center">
                    <PlusOutlined />{' '}
                    <strong>
                      {intl.formatMessage({
                        id: 'batch_code.select.newBatch',
                      })}
                    </strong>
                  </Flex>
                ),
              },
              ...(allowUnbatch
                ? [
                    {
                      value: 'unbatch',
                      label: (
                        <Flex gap={5} align="center">
                          <CloseOutlined style={{ color: token.colorError }} />{' '}
                          <strong style={{ color: token.colorError }}>
                            {intl.formatMessage({
                              id: 'batch_code.select.unbatch',
                            })}
                          </strong>
                        </Flex>
                      ),
                    },
                  ]
                : []),
              ...availableBatches.map((batch) => ({
                value: batch.id,
                label: (
                  <>
                    {`${batch.batch_code} --- `}
                    <span
                      style={{ color: '#999' }}
                    >{`(${batch.current_batch_volume}%)`}</span>
                  </>
                ),
              })),
            ]}
          />
        </AntdFormItem>
        <div style={{ flex: '0 0 49%' }}>
          <Flex gap={10}>
            <AntdFormItem
              label={intl.formatMessage({ id: 'concrete_quality' })}
              name="concrete_quality"
              style={{ flex: '0 0 32%' }}
              valuePropName="checked"
              defaultValue={false}
            >
              <Switch />
            </AntdFormItem>
            {normative === 'coe' && (
              <>
                <AntdFormItem
                  label={intl.formatMessage({ id: 'certified_dis' })}
                  name="certified_dis"
                  valuePropName="checked"
                  style={{ flex: '0 0 32%' }}
                  defaultValue={false}
                >
                  <Switch
                    onChange={(checked) => {
                      form.setFieldValue('delta', null);
                      setIsCertifiedDispersionChecked(checked);
                    }}
                  />
                </AntdFormItem>
                {isCertifiedDispersionChecked && (
                  <AntdFormItem
                    label={intl.formatMessage({ id: 'delta' })}
                    name="delta"
                    style={{ flex: '0 0 32%' }}
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: 'required',
                        }),
                      },
                    ]}
                  >
                    <AntdInput size="large" type="number" />
                  </AntdFormItem>
                )}
              </>
            )}
          </Flex>
        </div>
      </Flex>
    </Form>
  );
};

BatchForm.propTypes = {
  form: PropTypes.object.isRequired,
  onValuesChange: PropTypes.func.isRequired,
  structuralElements: PropTypes.array.isRequired,
  availableBatches: PropTypes.array.isRequired,
  allowUnbatch: PropTypes.bool.isRequired,
  normative: PropTypes.string.isRequired,
  deliveryNote: PropTypes.shape({
    mash_control_extra: PropTypes.bool,
    mash_control: PropTypes.bool,
    concrete_batch: PropTypes.number,
  }),
  fetchingBatches: PropTypes.bool,
};

BatchForm.defaultProps = {
  deliveryNote: undefined,
  fetchingBatches: true,
};

export default BatchForm;
